import React from 'react';
import './PrivacyPolicy.css';

interface PrivacyPolicyProps {
  onClose?: () => void;
  isModal?: boolean;
}

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onClose, isModal = false }) => {
  const content = (
    <div className={`privacy-policy-content ${isModal ? 'modal-content' : ''}`}>
      {isModal && (
        <div className="privacy-policy-header">
          <h1>Privacy Policy</h1>
          <button onClick={onClose} className="close-button" aria-label="Close">
            ×
          </button>
        </div>
      )}
      
      {!isModal && <h1>Privacy Policy</h1>}
      
      <div className="last-updated">
        <strong>Last Updated:</strong> October 9, 2025
      </div>

      <section>
        <h2>1. Introduction</h2>
        <p>Welcome to Unreleased ("we," "our," or "us"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our music feedback platform service.</p>
      </section>

      <section>
        <h2>2. Information We Collect</h2>
        
        <h3>2.1 Personal Information</h3>
        <p>When you create an account, we collect:</p>
        <ul>
          <li>Name (first and last name)</li>
          <li>Email address</li>
          <li>Profile information you choose to provide</li>
        </ul>

        <h3>2.2 Google Authentication</h3>
        <p>When you sign in with Google, we receive:</p>
        <ul>
          <li>Your Google account email address</li>
          <li>Your public profile information (name, profile picture)</li>
          <li>A unique identifier for your Google account</li>
        </ul>
        <p>We do not store your Google account password or access your other Google services.</p>

        <h3>2.3 Music and Feedback Data</h3>
        <p>When you use our platform, we collect:</p>
        <ul>
          <li>Audio files you upload for feedback</li>
          <li>Feedback you provide on other users' music</li>
          <li>Artist profile information you create</li>
          <li>Usage data and analytics</li>
        </ul>
      </section>

      <section>
        <h2>3. How We Use Your Information</h2>
        <p>We use your information to:</p>
        <ul>
          <li>Provide and maintain our music feedback service</li>
          <li>Create and manage your user account</li>
          <li>Enable you to upload music and receive feedback</li>
          <li>Allow you to provide feedback to other users</li>
          <li>Communicate with you about your account and our services</li>
          <li>Improve our platform and user experience</li>
          <li>Ensure platform security and prevent abuse</li>
        </ul>
      </section>

      <section>
        <h2>4. Information Sharing and Disclosure</h2>
        <p>We do not sell, trade, or rent your personal information to third parties. We may share information in the following circumstances:</p>
        <ul>
          <li><strong>With other users:</strong> Your artist profile and public feedback are visible to other platform users</li>
          <li><strong>Service providers:</strong> We may share data with trusted service providers who help us operate our platform</li>
          <li><strong>Legal requirements:</strong> We may disclose information if required by law or to protect our rights</li>
          <li><strong>Business transfers:</strong> In the event of a merger or acquisition, user data may be transferred</li>
        </ul>
      </section>

      <section>
        <h2>5. Data Security</h2>
        <p>We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.</p>
      </section>

      <section>
        <h2>6. Your Rights and Choices</h2>
        <p>You have the right to:</p>
        <ul>
          <li>Access and update your personal information</li>
          <li>Delete your account and associated data</li>
          <li>Opt out of certain communications</li>
          <li>Request a copy of your data</li>
          <li>Withdraw consent for data processing</li>
        </ul>
      </section>

      <section>
        <h2>7. Third-Party Services</h2>
        <p>Our platform integrates with Google Authentication services. Please review Google's Privacy Policy to understand how they handle your data: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></p>
      </section>

      <section>
        <h2>8. Children's Privacy</h2>
        <p>Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.</p>
      </section>

      <section>
        <h2>9. Changes to This Privacy Policy</h2>
        <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.</p>
      </section>

      <section>
        <h2>10. International Data Transfers</h2>
        <p>Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers.</p>
      </section>

      <section>
        <h2>11. Data Retention</h2>
        <p>We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law.</p>
      </section>

      <section className="contact-section">
        <h2>12. Contact Us</h2>
        <p>If you have any questions about this Privacy Policy or our data practices, please contact us at:</p>
        <p>
          <strong>Email:</strong> <a href="mailto:admin@unreleasedtracks.com">admin@unreleasedtracks.com</a><br />
          <strong>Website:</strong> <a href="/">https://unreleasedtracks.com</a>
        </p>
        <p>We are committed to resolving any privacy concerns you may have.</p>
      </section>
    </div>
  );

  if (isModal) {
    return (
      <div className="privacy-policy-modal-overlay" onClick={onClose}>
        <div className="privacy-policy-modal" onClick={(e) => e.stopPropagation()}>
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className="privacy-policy-container">
      {content}
    </div>
  );
};

export default PrivacyPolicy;